<script setup lang="ts">
export type ContentTab = {
    title: string;
    href: string;
};

const {
    tabs = [],
    stopElementId = '',
    itemsMinWidth = 600,
    offsetWidth = 350,
    scrollOffset = 0,
} = defineProps<IProps>();

interface IProps {
    tabs?: ContentTab[];
    stopElementId?: string;
    itemsMinWidth?: number;
    offsetWidth?: number;
    scrollOffset?: number;
}
const empty = ref<HTMLDivElement>();
const navigationContainer = ref<HTMLDivElement>();

const isStickyHeader = ref(false);
const hideHeader = ref(false);
const height = ref(0);
const maxWidth = ref(50000);
const scrollTo = (href: string) => {
    const element = document.getElementById(href.replace('#', ''));
    if (!element) {
        return;
    }
    const top =
        element.getBoundingClientRect().top + window.scrollY + scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });
};

defineExpose({
    scrollTo,
});
const initStickyHeader = () =>
    window.addEventListener('scroll', () => {
        if (!isStickyHeader.value) {
            isStickyHeader.value =
                (navigationContainer.value?.getBoundingClientRect().top || 0) <=
                0;
        } else {
            isStickyHeader.value =
                (empty.value?.getBoundingClientRect().top || 0) <= 0;
        }
        const stopElement = document.getElementById(stopElementId);
        hideHeader.value = !!(
            stopElement?.offsetTop &&
            stopElement.offsetTop - 160 <= window.pageYOffset
        );
    });
onMounted(() => {
    nextTick(() => {
        height.value = navigationContainer.value?.offsetHeight || 0;
        maxWidth.value =
            empty.value?.offsetWidth ||
            navigationContainer.value?.offsetWidth ||
            0;
    });
    initStickyHeader();
});
</script>

<template>
    <div class="content-navigation-root">
        <div
            ref="navigationContainer"
            class="navigation-wrapper"
            :class="{ sticky: isStickyHeader, 'hide-header': hideHeader }"
            :style="{
                '--min-width': `${itemsMinWidth}px`,
                '--offset-width': `${offsetWidth}px`,
            }"
        >
            <div
                class="navigation-container main"
                :style="{ width: `${maxWidth}px` }"
            >
                <div :style="{ 'max-width': `${maxWidth}px` }" class="my-nav">
                    <div
                        v-for="tab in tabs"
                        :key="tab.href"
                        class="scroll-active-item"
                        @click="scrollTo(tab.href)"
                    >
                        <mr-typography weight="500">
                            {{ tab.title }}
                        </mr-typography>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-show="isStickyHeader"
            ref="empty"
            :style="{ height: `${height}px` }"
        />
    </div>
</template>
<style lang="scss" scoped>
.navigation-wrapper {
    &::after {
        content: '';
        display: block;
        width: var(--offset-width);
    }
    display: flex;
    width: 100%;
    justify-content: stretch;
    top: 0;
    z-index: 100;
    &.sticky {
        position: fixed;
        top: 0;
        pointer-events: none;
        > .main {
            background-color: white;
            border-radius: 12px;
            @include shadow();
        }
    }
    transition: 0.4s linear;
    &.hide-header {
        top: -54px !important;
    }
    .navigation-container {
        width: 100%;
        z-index: 5;
        position: relative;
        top: 0;
        pointer-events: all;
        .my-nav {
            display: flex;
            min-width: var(--min-width);
            width: 100%;
            justify-content: stretch;
            .scroll-active-item {
                padding: 8px 16px;
                flex-grow: 1;
                display: flex;
                justify-content: center;
                white-space: nowrap;
                cursor: pointer;
                &:hover {
                    text-decoration-line: none;
                }
                border-bottom: 1px solid usePalette($p-cool-gray, $p-200);
            }
        }
    }
    .main {
        overflow-x: auto;
        @include no-scrollbar;
        width: 100%;
    }
}
</style>
